import { useDisclosure } from "@mantine/hooks";
import { useCallback, useMemo, useState } from "react";
import { Button } from "src/components/shared/Button/Button";
import { usePostApi } from "src/hooks";
import { type IEntryData } from "src/pages/Reports/Report/Report.types";
import { useSelector } from "src/redux/hooks";
import { type ILogbookCharts } from "src/redux/slices/entriesGeneratorSlice";
import { getObjectKeys, getYachtId } from "src/utils";

import CreateChartModal from "./CreateChartModal";

interface Props {
  start: string;
  end: string;
  status: number;
}

const CreateChartButton = ({ start, end }: Props) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [data, setData] = useState<ILogbookCharts[]>([]);
  const { post, isLoading } = usePostApi();

  const { selectedCols } = useSelector(state => state.entriesGeneratorSlice);

  const handleCreateChart = useCallback(async () => {
    const spec: Record<string, Array<{ section: number; col: string }>> = {};
    const logs = getObjectKeys(selectedCols);

    logs.forEach((log: string) => {
      getObjectKeys(selectedCols[log]).forEach((sIndex: number) => {
        selectedCols[log][sIndex].forEach((name: string) => {
          if (!spec[log]) spec[log] = [];
          spec[log].push({ section: Number(sIndex) + 1, col: name });
        });
      });
    });
    const promises = logs.map(async log => {
      const data = await post({
        request: "get-entry-data",
        body: {
          params: {
            lbid: log,
            yachtid: getYachtId(),
            status: null,
            start,
            end,
            spec: spec[log],
          },
        },
      });

      return { lbid: log, data };
    });

    const results = await Promise.all(promises);

    const chartsDataObj: Record<
      string,
      Record<string, Array<string | number>>
    > = {};

    results.map(({ lbid, data }) =>
      (data?.data as IEntryData[])?.forEach(entry => {
        entry.vals.forEach(col => {
          getObjectKeys(col).forEach(name => {
            if (!chartsDataObj[lbid]) chartsDataObj[lbid] = {};
            if (!chartsDataObj[lbid][name]) chartsDataObj[lbid][name] = [];

            col[name].forEach(record => {
              chartsDataObj[lbid][name].push(record.val);
            });
          });
        });
      }),
    );

    setData(
      Object.entries(chartsDataObj).map(([lbid, data]) => ({
        lbid,
        data: getObjectKeys(data).map(name => ({
          name,
          type: "line",
          vals: data[name],
          trendline: false,
        })),
      })),
    );

    open();
  }, [end, open, post, selectedCols, start]);

  const disabled = useMemo(
    () =>
      !Object.values(selectedCols).some(log =>
        Object.values(log).some(sec => sec.length > 0),
      ),
    [selectedCols],
  );

  return (
    <>
      <Button
        disabled={disabled}
        onClick={handleCreateChart}
        loading={isLoading}>
        Create Chart
      </Button>
      <CreateChartModal
        opened={opened}
        close={close}
        data={data}
        setData={setData}
      />
    </>
  );
};

export default CreateChartButton;
